import { useState } from 'react';

import { useCreateMailpiecesListCommentMutation, useUpdatePiecesMutation } from '@api/piece';
import { Piece, PieceAction } from '@apiContract';

import { apiRequest } from '@src/api/apiRequest';
import { uploadImageToS3 } from '@src/api/imageS3';
import { getUploadParamsForImage } from '@components/MailpieceActions/components/actions/FinishDeliveryWithImage/helper';
import { ValidationData } from '@components/MailpieceActions/types';
import { buildValidationPieces } from '@components/MailpieceActions/utils';
import { resizeImage } from '@utils/image';

const actionTypes = {
  delivery: PieceAction.FinishDeliveryWithImage,
  pickup: PieceAction.FinishPickupWithImage,
}

const keyTypes = {
  delivery: 'deliveryImageKey',
  pickup: 'pickupImageKey',
}

export const useUploadImage = (
  mailpieces: Piece[],
  onSuccess: () => void,
  type?: string,
  validationData?: ValidationData,
) => {
  const [updatePieces] = useUpdatePiecesMutation();
  const [createMailpiecesListComment] = useCreateMailpiecesListCommentMutation();
  const [loading, setLoading] = useState(false);

  const uploadImage = async (imageSrc: string, skipNotifyRecipient?: boolean, comment?: string, notifyMailroom?: boolean) => {
    try {
      setLoading(true);
      const { url, key } = await getUploadParamsForImage(mailpieces[0].id);
      const resizedImage = await resizeImage(imageSrc, 4096, 4096);

      await apiRequest(
        uploadImageToS3,
        {
          image: resizedImage,
          uploadUrl: url,
          contentType: 'image/jpg',
        },
        false,
        5,
      );

      await updatePieces({
        pieces: buildValidationPieces(mailpieces, validationData),
        action: actionTypes[type as keyof typeof actionTypes],
        [keyTypes[type as keyof typeof keyTypes]]: key,
        ...(skipNotifyRecipient !== undefined && { shouldNotifyRecipient: !skipNotifyRecipient }),
      }).unwrap();

      if (comment?.trim().length) {
        await createMailpiecesListComment({
          text: comment.trim(),
          notifyMailroom: notifyMailroom ?? true,
          pieceIds: mailpieces.map((piece) => piece.id),
        }).unwrap();
      }

      onSuccess();
    } catch (error) {
      console.error('Failed to process image upload:', error);
    } finally {
      setLoading(false);
    }
  };

  return { uploadImage, loading };
};
